import React, { useState, useEffect } from 'react';
import { adminApi } from '../api/adminApi';
import toast, { Toaster } from 'react-hot-toast';
import PageHeader from '../components/common/PageHeader';
import { Cog8ToothIcon, CurrencyRupeeIcon, GlobeAltIcon, CreditCardIcon } from '@heroicons/react/24/outline';

const Settings = () => {
  const [settings, setSettings] = useState({
    platformName: 'Khatabook',
    maintenanceMode: 'false',
    defaultCurrency: 'INR',
    gstRate: '18',
    razorpayKey: '',
    supportEmail: 'support@khatabook.com'
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await adminApi.getSettings();
      if (res.data.data) {
        const formattedSettings = {};
        if (Array.isArray(res.data.data)) {
          res.data.data.forEach(s => {
            formattedSettings[s.key] = s.value;
          });
        } else {
          Object.assign(formattedSettings, res.data.data);
        }
        setSettings(prev => ({ ...prev, ...formattedSettings }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const promises = Object.keys(settings).map(key => 
        adminApi.updateSetting(key, settings[key])
      );
      await Promise.all(promises);
      toast.success('Settings updated successfully');
    } catch (error) {
      toast.error('Failed to update some settings');
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'general', name: 'General', icon: GlobeAltIcon },
    { id: 'finance', name: 'Finance & Tax', icon: CurrencyRupeeIcon },
    { id: 'payment', name: 'Payment Gateways', icon: CreditCardIcon },
    { id: 'system', name: 'System', icon: Cog8ToothIcon }
  ];

  if (loading) {
    return <div className="min-h-screen p-12 text-center text-gray-500 font-bold">Loading settings...</div>;
  }

  return (
    <div className="min-h-screen bg-transparent pb-12">
      <Toaster position="top-right" />
      
      <PageHeader 
        title="Platform Settings"
        subtitle="Manage global configuration, taxes, and integrations."
      />

      <main className="max-w-7xl">
        <div className="bg-white rounded-[14px] shadow-[0_8px_30px_rgb(0,0,0,0.02)] overflow-hidden border-t-[6px] border-red-600 flex flex-col md:flex-row min-h-[600px]">
          
          {/* Sidebar Tabs */}
          <div className="w-full md:w-64 bg-gray-50 border-r border-gray-100 p-6 flex flex-col space-y-2">
            {tabs.map(tab => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center w-full px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                    isActive 
                      ? 'bg-red-50 text-red-700 shadow-sm' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <tab.icon className={`h-5 w-5 mr-3 ${isActive ? 'text-red-500' : 'text-gray-400'}`} />
                  {tab.name}
                </button>
              )
            })}
          </div>

          {/* Form Content */}
          <div className="flex-1 p-8">
            <form onSubmit={handleSave} className="h-full flex flex-col">
              
              <div className="flex-1">
                {activeTab === 'general' && (
                  <div className="space-y-6 max-w-lg animate-fadeIn">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1.5">Platform Name</label>
                      <input 
                        type="text" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500 font-bold text-gray-900 transition-all outline-none" 
                        value={settings.platformName} onChange={e => setSettings({...settings, platformName: e.target.value})} 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1.5">Support Email</label>
                      <input 
                        type="email" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500 font-bold text-gray-900 transition-all outline-none" 
                        value={settings.supportEmail} onChange={e => setSettings({...settings, supportEmail: e.target.value})} 
                      />
                    </div>
                  </div>
                )}

                {activeTab === 'finance' && (
                  <div className="space-y-6 max-w-lg animate-fadeIn">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1.5">Default Currency</label>
                      <select 
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500 font-bold text-gray-900 transition-all outline-none"
                        value={settings.defaultCurrency} onChange={e => setSettings({...settings, defaultCurrency: e.target.value})}
                      >
                        <option value="INR">INR (₹)</option>
                        <option value="USD">USD ($)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1.5">Default GST/Tax Rate (%)</label>
                      <input 
                        type="number" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500 font-bold text-gray-900 transition-all outline-none" 
                        value={settings.gstRate} onChange={e => setSettings({...settings, gstRate: e.target.value})} 
                      />
                    </div>
                  </div>
                )}

                {activeTab === 'payment' && (
                  <div className="space-y-6 max-w-lg animate-fadeIn">
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl flex">
                      <p className="text-sm font-medium text-yellow-800">Note: Updating API keys will immediately affect all live transactions on the platform.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1.5">Razorpay Live API Key</label>
                      <input 
                        type="password" placeholder="rzp_live_xxxxxxxxxxx" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500 font-bold text-gray-900 transition-all outline-none" 
                        value={settings.razorpayKey} onChange={e => setSettings({...settings, razorpayKey: e.target.value})} 
                      />
                    </div>
                  </div>
                )}

                {activeTab === 'system' && (
                  <div className="space-y-6 max-w-lg animate-fadeIn">
                    <label className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 cursor-pointer hover:border-red-200 transition-colors">
                      <div>
                        <span className="text-sm font-extrabold text-gray-900 block">Maintenance Mode</span>
                        <span className="text-xs font-medium text-gray-500">Disable access for non-admin users</span>
                      </div>
                      <div className="relative flex items-center">
                        <input 
                          type="checkbox" 
                          checked={settings.maintenanceMode === 'true'} 
                          onChange={e => setSettings({...settings, maintenanceMode: e.target.checked ? 'true' : 'false'})} 
                          className="peer sr-only" 
                        />
                        <div className={`w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-red-600 transition-all after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5 peer-checked:after:border-white`}></div>
                      </div>
                    </label>
                  </div>
                )}
              </div>

              <div className="pt-8 mt-auto border-t border-gray-100 flex justify-end">
                <button 
                  type="submit" 
                  disabled={isSaving}
                  className="px-8 py-3 rounded-full text-sm font-bold bg-red-600 text-white hover:bg-red-700 shadow-md transition-all transform hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:transform-none"
                >
                  {isSaving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>

            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings;
